import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import Card from "@midland-industries/Components/Card";
import Group from "@midland-industries/Components/Group";
import Stack from "@midland-industries/Components/Stack";
import { colors } from "@midland-industries/Styles/colors";
import React from "react";
import styled from "styled-components";

// Create a styled wrapper for the card with hover effects
const CardWrapper = styled.div`
    position: relative;
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    
    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
`;

// Create a styled link that doesn't show underlines
const StyledLink = styled(Link)`
    text-decoration: none;
    display: block;
    
    &:hover {
        text-decoration: none;
        
        .shop-now-text {
            font-weight: 700;
        }
    }
`;

// Create a styled link for special links with icons
const StyledIconLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    margin-top: 10px;
    font-size: 14px;
    font-weight: 500;
    text-decoration: none;
    color: ${colors.primary};
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    &:hover {
        background-color: #e6f2ff;
        text-decoration: none;
        font-weight: 700;
    }
`;

// Special version for the header card with no top margin
const HeaderStyledIconLink = styled(StyledIconLink)`
    margin-top: 0;
`;

// Brochure link with document emoji
const BrochureLink = (props: React.ComponentProps<typeof Link>) => (
    <StyledIconLink {...props}>
        <span style={{ fontSize: '16px', marginRight: '5px' }}>📄</span>
        {props.children}
    </StyledIconLink>
);

// View all link with document emoji (same as brochure link)
const ViewAllLink = (props: React.ComponentProps<typeof Link> & { isHeader?: boolean }) => {
    const LinkComponent = props.isHeader ? HeaderStyledIconLink : StyledIconLink;
    return (
        <LinkComponent {...props}>
            <span style={{ fontSize: '16px', marginRight: '5px' }}>📄</span>
            {props.children}
        </LinkComponent>
    );
};

const StandardCardWrapper = styled.div<{ width: string }>`
    flex: 1;
    max-width: ${props => props.width};
    @media (max-width: 768px) {
        max-width: 100%;
        margin-bottom: 15px;
    }
`;

const CardRow = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 20px;
    
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

// Custom header card component
const CustomHeaderCard = styled.div<{ bgColor?: string }>`
    width: 100%;
    padding: 12px 20px;
    border-radius: 10px;
    background-color: ${props => props.bgColor || colors.background} !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const enum fields {
    widgetHeading = "widgetHeading",
    viewAllLink = "viewAllLink",
    viewAllLinkText = "viewAllLinkText",
    attributeValueIds = "attributeValueIds",
    rows = "rows",
    rowTitle = "rowTitle",
    rowCards = "rowCards",
    headerBackgroundColor = "headerBackgroundColor",
    headerTextColor = "headerTextColor",
}

interface CardModel {
    fields: {
        cardTitle: string;
        heading: string;
        subheading: string;
        content: string;
        shopNowLink: LinkFieldValue;
        brochureLink?: LinkFieldValue;
        contentImage: string;
    };
}

interface RowModel {
    fields: {
        [fields.rowTitle]: string;
        [fields.rowCards]: CardModel[];
    };
}

interface FeaturedCategoriesTableProps extends WidgetProps {
    fields: {
        [fields.widgetHeading]: string;
        [fields.viewAllLink]: LinkFieldValue;
        [fields.viewAllLinkText]: string;
        [fields.attributeValueIds]: string;
        [fields.rows]: RowModel[];
        [fields.headerBackgroundColor]: string;
        [fields.headerTextColor]: string;
    };
}

const FeaturedCategoriesTable: React.FC<FeaturedCategoriesTableProps> = ({ 
    fields: { 
        widgetHeading, 
        viewAllLink, 
        viewAllLinkText, 
        attributeValueIds, 
        rows,
        headerBackgroundColor,
        headerTextColor
    } 
}) => {
    // Check if we have any content to display
    const hasConfiguredRows = rows && rows.length > 0;
    
    if (!hasConfiguredRows) {
        return null;
    }

    // Collect all cards from all rows
    const allRowCards: CardModel[] = [];
    rows.forEach((row: RowModel) => {
        if (row.fields.rowCards) {
            allRowCards.push(...row.fields.rowCards);
        }
    });
    
    const rowCardLinks = useGetLinks(
        allRowCards,
        (card: CardModel) => card.fields.shopNowLink
    );
    
    // Filter cards with brochure links and resolve them
    const rowCardsWithBrochure = allRowCards.filter(card => card.fields.brochureLink && card.fields.brochureLink.value);
    const rowCardBrochureLinks = useGetLinks(
        rowCardsWithBrochure,
        (card: CardModel) => card.fields.brochureLink as LinkFieldValue
    );

    // Resolve the viewAllLink
    const [resolvedViewAllLink] = useGetLinks([viewAllLink], link => link);

    // Check if we're on mobile
    const [isMobile, setIsMobile] = React.useState(false);
    
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    // Helper function to calculate optimal cards per row
    const getOptimalCardsPerRow = (cardCount: number) => {
        // Logic to determine optimal cards per row based on count
        if (cardCount <= 2) { return cardCount; }
        if (cardCount === 3) { return 3; }
        if (cardCount === 4) { return 4; }
        if (cardCount <= 6) { return 3; }
        return 4; // Default to 4 for larger collections
    };
    
    // Calculate card width based on number of cards per row
    const getCardWidth = (cardsPerRow: number) => {
        const gapWidth = 15; // Gap between cards in pixels
        return `calc(${100 / cardsPerRow}% - ${(gapWidth * (cardsPerRow - 1)) / cardsPerRow}px)`;
    };

    // Render a standard card
    const renderStandardCard = (card: CardModel, index: number, cardsPerRow: number, links: any[] = rowCardLinks, brochureLinksArray: any[] = rowCardBrochureLinks, rowHasContent = false) => {
        const shopNowUrl = links[index]?.url 
            ? `${links[index].url}${links[index].url.includes('?') ? '&' : '?'}attributeValueIds=${attributeValueIds}` 
            : "#";
        
        const cardWidth = getCardWidth(cardsPerRow);
        
        // Check if this card has a brochure link
        const hasBrochureLink = card.fields.brochureLink && card.fields.brochureLink.value;
        
        // Find the brochure link URL if available
        let brochureUrl = "#";
        if (hasBrochureLink) {
            // Find the index of this card in the appropriate brochure links array
            const cardWithBrochureIndex = rowCardsWithBrochure.findIndex(c => 
                c.fields.brochureLink?.value === card.fields.brochureLink?.value);
            
            if (cardWithBrochureIndex !== -1 && brochureLinksArray[cardWithBrochureIndex]?.url) {
                brochureUrl = brochureLinksArray[cardWithBrochureIndex].url;
            }
        }
                
        return (
        <StandardCardWrapper width={cardWidth}>
            <StyledLink key={`standard-${index}`} href={shopNowUrl}>
                <CardWrapper>
                    <Card
                        height={isMobile ? undefined : rowHasContent || card.fields.content ? 450 : 380}
                        borderRadius={10}
                        cardStyles={{ 
                            padding: isMobile ? "20px" : "20px 20px 15px 20px",
                            display: "flex",
                            flexDirection: cardsPerRow === 1 && !isMobile ? "row" : "column"
                        }}
                        gap={8}
                    >
                        {/* For single card layout, wrap text content in a container */}
                        <Stack 
                            style={{ 
                                width: "100%", 
                                maxWidth: cardsPerRow === 1 && !isMobile ? "40%" : 427,
                                marginRight: cardsPerRow === 1 && !isMobile ? "20px" : 0
                            }} 
                            gap={6}
                        >
                            <Typography 
                                style={{ 
                                    wordBreak: "break-word", 
                                    width: "100%",
                                    fontSize: isMobile ? "14px" : "14px",
                                    lineHeight: isMobile ? "20px" : "20px"
                                }} 
                                color={colors.textAccent} 
                                transform="uppercase"
                            >
                                {card.fields.heading}
                            </Typography>
                            <Typography 
                                style={{ 
                                    wordBreak: "break-word", 
                                    width: "100%",
                                    fontSize: isMobile ? "22px" : 24,
                                    lineHeight: isMobile ? "26px" : "28px"
                                }} 
                                weight={700} 
                                color={colors.textMain}
                            >
                                {card.fields.subheading}
                            </Typography>
                            <Typography 
                                style={{ 
                                    wordBreak: "break-word", 
                                    width: "100%",
                                    fontSize: isMobile ? "14px" : "14px",
                                    lineHeight: isMobile ? "20px" : "20px"
                                }} 
                                color={colors.textAccent}
                            >
                                {card.fields.content}
                            </Typography>
                            <Typography 
                                color="primary" 
                                style={{ textDecoration: "underline" }} 
                                className="shop-now-text"
                            >
                                Shop now
                            </Typography>
                            
                            {/* Display brochure link under Shop Now for single card layout */}
                            {hasBrochureLink && cardsPerRow === 1 && !isMobile && (
                                <div onClick={(e) => e.stopPropagation()} style={{ marginTop: "10px" }}>
                                    <BrochureLink href={brochureUrl}>
                                        Product Brochure
                                    </BrochureLink>
                                </div>
                            )}
                        </Stack>
                        <Stack 
                            justify="center" 
                            align="center" 
                            style={{ 
                                overflow: "hidden", 
                                marginTop: cardsPerRow === 1 && !isMobile ? 0 : "auto",
                                width: "100%",
                                maxHeight: cardsPerRow === 1 && !isMobile ? "none" : (rowHasContent || card.fields.content ? "300px" : "280px"),
                                flex: 1
                            }}
                        >
                            <Img
                                src={card.fields.contentImage}
                                style={{ 
                                    maxWidth: "100%", 
                                    maxHeight: cardsPerRow === 1 && !isMobile ? "350px" : "100%",
                                    height: "auto", 
                                    objectFit: "contain" 
                                }}
                            />
                        </Stack>
                        {/* Display brochure link at bottom for multi-card layout */}
                        {hasBrochureLink && (cardsPerRow !== 1 || isMobile) && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                                <BrochureLink href={brochureUrl}>
                                    Product Brochure
                                </BrochureLink>
                            </div>
                        )}
                    </Card>
                </CardWrapper>
            </StyledLink>
        </StandardCardWrapper>
        );
    };

    // Render configured rows with dynamic cards per row
    const renderConfiguredRows = () => {
        if (!hasConfiguredRows) {
            return null;
        }
        
        let linkIndex = 0;
        
        return rows.map((row, rowIndex) => {
            const rowCards = row.fields.rowCards || [];
            
            if (rowCards.length === 0) {
                return null;
            }
            
            // Dynamically determine cards per row based on count
            const dynamicCardsPerRow = getOptimalCardsPerRow(rowCards.length);
            
            // Check if any card in this row has content
            const anyCardHasContent = rowCards.some(card => card.fields.content);
            
            const renderedCards = rowCards.map((card, cardIndex) => {
                const renderedCard = renderStandardCard(
                    card, 
                    linkIndex, 
                    dynamicCardsPerRow,
                    rowCardLinks,
                    rowCardBrochureLinks,
                    anyCardHasContent
                );
                linkIndex++;
                return renderedCard;
            });
            
            return (
                <CardRow key={`configured-row-${rowIndex}`}>
                    {renderedCards}
                </CardRow>
            );
        });
    };

    return (
        <Stack gap={15}>
            <CardRow>
                <StandardCardWrapper width="100%">
                    <CustomHeaderCard bgColor={headerBackgroundColor || colors.background}>
                        <Typography 
                            size={isMobile ? 20 : 24} 
                            transform="uppercase"
                            style={{ 
                                color: headerTextColor || colors.textAccent,
                                fontWeight: 700
                            }}
                        >
                            {widgetHeading}
                        </Typography>
                        {resolvedViewAllLink.url && (
                            <ViewAllLink 
                                href={resolvedViewAllLink.url}
                                isHeader={true}
                                style={{ fontSize: isMobile ? 16 : 18 }}
                            >
                                {viewAllLinkText}
                            </ViewAllLink>
                        )}
                    </CustomHeaderCard>
                </StandardCardWrapper>
            </CardRow>
            
            {/* Render configured rows */}
            {renderConfiguredRows()}
        </Stack>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};
const rowsTab = {
    displayName: "Rows",
    sortOrder: 1,
};
const colorsTab = {
    displayName: "Colors",
    sortOrder: 2,
};

const featuredCategoriesTable: WidgetModule = {
    component: FeaturedCategoriesTable,
    definition: {
        displayName: "Featured Categories Table",
        icon: "row",
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.widgetHeading,
                editorTemplate: "TextField",
                displayName: "Widget Heading",
                defaultValue: "Featured Categories Table",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Products Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.viewAllLinkText,
                editorTemplate: "TextField",
                displayName: "View All Link Text",
                defaultValue: "View all new products",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.attributeValueIds,
                editorTemplate: "TextField",
                displayName: "New Product Attribute Value ID",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.rows,
                editorTemplate: "ListField",
                displayName: "Configurable Rows",
                getDisplay: () => "Row",
                defaultValue: [],
                fieldType: "Translatable",
                tab: rowsTab,
                fieldDefinitions: [
                    {
                        name: fields.rowCards,
                        editorTemplate: "ListField",
                        displayName: "Cards",
                        getDisplay: ({ fields: { cardTitle } }) => cardTitle || "Card",
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "cardTitle",
                                editorTemplate: "TextField",
                                defaultValue: "Card",
                            },
                            {
                                name: "heading",
                                editorTemplate: "TextField",
                                displayName: "Heading",
                                defaultValue: "",
                            },
                            {
                                name: "subheading",
                                editorTemplate: "TextField",
                                displayName: "Subheading",
                                defaultValue: "",
                            },
                            {
                                name: "content",
                                displayName: "Content",
                                editorTemplate: "MultilineTextField",
                                defaultValue: "",
                            },
                            {
                                name: "shopNowLink",
                                displayName: "Shop Now Link",
                                editorTemplate: "LinkField",
                                defaultValue: { type: "Page", value: "" },
                            },
                            {
                                name: "contentImage",
                                displayName: "Content Image",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                            },
                            {
                                name: "brochureLink",
                                displayName: "Brochure Link",
                                editorTemplate: "LinkField",
                                defaultValue: { type: "Page", value: "" },
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.headerBackgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Header Background Color",
                defaultValue: colors.background,
                fieldType: "General",
                tab: colorsTab,
            },
            {
                name: fields.headerTextColor,
                editorTemplate: "ColorPickerField",
                displayName: "Header Text Color",
                defaultValue: colors.textAccent,
                fieldType: "General",
                tab: colorsTab,
            },
        ],
    },
};

export default featuredCategoriesTable;
